import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { API } from '../lib/api';
import { toast } from 'sonner';

const AddressContext = createContext();

export function AddressProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = !!user;
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultAddress, setDefaultAddress] = useState(null);

  // Load addresses when user logs in
  useEffect(() => {
    if (authLoading) return;
    
    const loadAddresses = async () => {
      if (!isAuthenticated) {
        setAddresses([]);
        setDefaultAddress(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await API.get('/addresses');
        const fetchedAddresses = response.data || [];
        console.log('Loaded addresses from API:', fetchedAddresses);
        setAddresses(fetchedAddresses);
        
        // Find default address
        const defaultAddr = fetchedAddresses.find(addr => addr.is_default);
        setDefaultAddress(defaultAddr || fetchedAddresses[0] || null);
      } catch (error) {
        console.error('Error loading addresses:', error);
        setAddresses([]);
        setDefaultAddress(null);
      } finally {
        setLoading(false);
      }
    };

    loadAddresses();
  }, [isAuthenticated, authLoading]);

  // Add new address
  const addAddress = useCallback(async (addressData) => {
    if (!isAuthenticated) {
      toast.error('Please login to add address');
      return null;
    }

    console.log('Adding address with data:', addressData);
    try {
      const response = await API.post('/addresses', addressData);
      console.log('Address created response:', response.data);
      const newAddress = response.data;
      
      setAddresses(prev => {
        if (newAddress.is_default) {
          return [newAddress, ...prev.map(addr => ({ ...addr, is_default: false }))];
        }
        return [newAddress, ...prev];
      });
      
      if (newAddress.is_default) {
        setDefaultAddress(newAddress);
      } else if (addresses.length === 0) {
        setDefaultAddress(newAddress);
      }
      
      toast.success('Address added successfully');
      return newAddress;
    } catch (error) {
      console.error('Error adding address:', error);
      toast.error(error.response?.data?.detail || 'Failed to add address');
      return null;
    }
  }, [isAuthenticated, addresses.length]);

  // Update existing address
  const updateAddress = useCallback(async (addressId, addressData) => {
    if (!isAuthenticated) {
      toast.error('Please login to update address');
      return null;
    }

    console.log('Updating address', addressId, 'with data:', addressData);
    try {
      const response = await API.patch(`/addresses/${addressId}`, addressData);
      console.log('Address updated response:', response.data);
      const updatedAddress = response.data;
      
      setAddresses(prev => {
        if (updatedAddress.is_default) {
          return prev.map(addr => addr.id === addressId
            ? updatedAddress
            : { ...addr, is_default: false }
          );
        }
        return prev.map(addr => addr.id === addressId ? updatedAddress : addr);
      });
      
      if (updatedAddress.is_default) {
        setDefaultAddress(updatedAddress);
      }
      
      toast.success('Address updated successfully');
      return updatedAddress;
    } catch (error) {
      console.error('Error updating address:', error);
      toast.error(error.response?.data?.detail || 'Failed to update address');
      return null;
    }
  }, [isAuthenticated]);

  // Delete address
  const deleteAddress = useCallback(async (addressId) => {
    if (!isAuthenticated) {
      toast.error('Please login to delete address');
      return false;
    }

    try {
      await API.delete(`/addresses/${addressId}`);
      
      setAddresses(prev => {
        const updated = prev.filter(addr => addr.id !== addressId);
        
        // If deleted address was default, set new default
        if (defaultAddress?.id === addressId && updated.length > 0) {
          setDefaultAddress(updated[0]);
        } else if (updated.length === 0) {
          setDefaultAddress(null);
        }
        
        return updated;
      });
      
      toast.success('Address deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting address:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete address');
      return false;
    }
  }, [isAuthenticated, defaultAddress]);

  // Set default address
  const setAsDefault = useCallback(async (addressId) => {
    if (!isAuthenticated) {
      toast.error('Please login');
      return false;
    }

    try {
      await API.post(`/addresses/${addressId}/set-default`);
      
      setAddresses(prev => 
        prev.map(addr => ({
          ...addr,
          is_default: addr.id === addressId
        }))
      );
      
      const newDefault = addresses.find(addr => addr.id === addressId);
      if (newDefault) {
        setDefaultAddress(newDefault);
      }
      
      toast.success('Default address updated');
      return true;
    } catch (error) {
      console.error('Error setting default address:', error);
      toast.error(error.response?.data?.detail || 'Failed to update default address');
      return false;
    }
  }, [isAuthenticated, addresses]);

  // Get address by ID
  const getAddress = useCallback((addressId) => {
    return addresses.find(addr => addr.id === addressId);
  }, [addresses]);

  return (
    <AddressContext.Provider value={{
      addresses,
      defaultAddress,
      loading,
      addAddress,
      updateAddress,
      deleteAddress,
      setAsDefault,
      getAddress
    }}>
      {children}
    </AddressContext.Provider>
  );
}

export function useAddresses() {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddresses must be used within AddressProvider');
  }
  return context;
}
